import { createContext, ReactNode, useContext, useMemo } from 'react';

export type SpreadsheetFeature = 'addRows' | 'deleteRows';

/**
 * Optional E2E hooks. The shared SpreadsheetEditor stays domain-agnostic; callers
 * pass a save-button testid, a per-cell testid factory, and a per-column-header
 * testid factory so each consuming domain (e.g. price list) can scope its own
 * selectors. See `.claude/rules/e2e-testing.md`, TV1-1529 §"data-testid
 * Requirements", and TV1-1682 §"data-testid requirements".
 */
type CellTestIdFn = ( ctx: { rowId: string; itemId?: string; columnId: string }) => string | undefined;
type HeaderTestIdFn = ( ctx: { columnId: string }) => string | undefined;

/**
 * Per-row streaming status surfaced by the row container (TV1-1877).
 *
 * Optional. When a caller supplies `rowStatusFn`, each visible row tints itself
 * green (`success`) / red (`failed`) / subtle in-flight (`pending`) based on the
 * lookup result. `failed` may carry an `error` whose `message` is shown in a
 * tooltip. Returning `undefined` for a row leaves it visually unchanged — this
 * is what every existing consumer (e.g. inventory bulk edit) gets, since they
 * never pass the function.
 */
export type RowStatusInfo = {
  status: 'pending' | 'success' | 'failed';
  error?: { message: string; name: string };
};
type RowStatusFn = ( rowId: string ) => RowStatusInfo | undefined;

/**
 * Optional imperative "scroll to a row by id" request (TV1-1877).
 *
 * Domain-agnostic. When a caller supplies `scrollToRow`, the grid body scrolls
 * the virtualized list so the row whose id matches `id` is centered. The
 * `nonce` is a monotonically increasing counter: it lets the SAME `id` be
 * re-triggered (e.g. jump to the same errored row twice). Omitting the prop is
 * unchanged behaviour for every other consumer.
 */
export type ScrollToRowRequest = { id: string; nonce: number };

type SpreadsheetConfig = {
  disabledFeatures: Set<SpreadsheetFeature>;
  saveButtonTestId?: string;
  cellTestIdFn?: CellTestIdFn;
  headerTestIdFn?: HeaderTestIdFn;
  rowStatusFn?: RowStatusFn;
  scrollToRow?: ScrollToRowRequest;
};

const SpreadsheetConfigContext = createContext<SpreadsheetConfig>({
  disabledFeatures: new Set()
});

export const useSpreadsheetConfig = () => useContext( SpreadsheetConfigContext );

export const SpreadsheetConfigProvider = ({
  disabledFeatures = [],
  saveButtonTestId,
  cellTestIdFn,
  headerTestIdFn,
  rowStatusFn,
  scrollToRow,
  children
}: {
  disabledFeatures?: SpreadsheetFeature[];
  saveButtonTestId?: string;
  cellTestIdFn?: CellTestIdFn;
  headerTestIdFn?: HeaderTestIdFn;
  rowStatusFn?: RowStatusFn;
  scrollToRow?: ScrollToRowRequest;
  children: ReactNode;
}) => {
  const config = useMemo(() => ({
    disabledFeatures: new Set( disabledFeatures ),
    saveButtonTestId,
    cellTestIdFn,
    headerTestIdFn,
    rowStatusFn,
    scrollToRow
  }), [ disabledFeatures, saveButtonTestId, cellTestIdFn, headerTestIdFn, rowStatusFn, scrollToRow ]);

  return (
    <SpreadsheetConfigContext.Provider value={config}>
      {children}
    </SpreadsheetConfigContext.Provider>
  );
};
